/**
 * Where a user goes once they sign in: the page a signed-out visit was
 * bounced from (a shared People Finder search, say). It lives in
 * sessionStorage so it survives the Google / Microsoft sign-in, which leaves
 * the app and comes back.
 */
export const POST_AUTH_REDIRECT_KEY = 'redirectAfterAuth';
/** Set while the stored redirect is the Slack link flow's `/integrations`. */
export const SLACK_REDIRECT_MARKER_KEY = 'cleanlist:slack-link-redirect';

/**
 * The path and query of `value` when it stays on this app, else null: an
 * absolute or protocol-relative URL ('https://…', '//host', '/\host') would
 * make the sign-in page an open redirect, and an auth page would loop.
 */
export function safePostAuthPath(value: string | null): string | null {
  if (!value?.startsWith('/')) return null;
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    if (url.pathname.startsWith('/auth')) return null;
    return `${url.pathname}${url.search}`;
  } catch {
    return null;
  }
}

// True once this page load has had a signed-in user. Losing the session after
// that is a sign-out, not a signed-out visit to a link: Clerk reports signed
// out a moment before it leaves the page, and remembering that page (a list
// in one workspace) sent the next sign-in back to it in whichever workspace
// was active then ("Lead list not found"). A session that expires mid-page
// also lands on Home after sign-in; saving the org with the path would let
// that case return to the page, if it ever matters.
let signedInThisPage = false;

export function markSignedInThisPage(): void {
  signedInThisPage = true;
}

export function rememberPostAuthRedirect(path: string): void {
  if (signedInThisPage) return;
  const safe = safePostAuthPath(path);
  // Home is where sign-in lands anyway.
  if (!safe || safe === '/') return;
  try {
    // A pending Slack link keeps its /integrations: its consent clears it.
    if (
      sessionStorage.getItem(SLACK_REDIRECT_MARKER_KEY) &&
      sessionStorage.getItem(POST_AUTH_REDIRECT_KEY)
    )
      return;
    sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, safe);
  } catch {
    // Storage blocked: sign-in still lands on Home.
  }
}

export function peekPostAuthRedirect(): string | null {
  try {
    return safePostAuthPath(sessionStorage.getItem(POST_AUTH_REDIRECT_KEY));
  } catch {
    return null;
  }
}

export function consumePostAuthRedirect(): string | null {
  const path = peekPostAuthRedirect();
  try {
    sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);
  } catch {
    // Nothing stored that could be read either.
  }
  return path;
}
